import {
  DialogContent,
  Dialog,
  DialogProps,
  DialogActions,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { PmiButton } from 'shell/components/shared/button';
import { styled } from '@mui/styles';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import * as yup from 'yup';
import SchedulationTypeList from './schedulation-type';
import { SchedulationItemVM } from './schedulation-table';
import EventJobList from './event-job-dropdown';

const validationSchema = yup.object({});

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

interface ModalInsertSchedulationDataProps {
  modalData: SchedulationItemVM | null;
  open: boolean;
  jobName?: string;
  disabled?: boolean;
  onClose: (open: boolean) => void;
  onSubmit: (values: SchedulationItemVM) => void;
}

const ModalInsertSchedulationData = ({
  modalData,
  open,
  onClose,
  onSubmit,
  jobName,
  disabled,
}: ModalInsertSchedulationDataProps): React.ReactElement => {
  const form = useFormik({
    initialValues: modalData || {
      code: '',
      description: '',
      schedulationType: '',
      cronExpression: '',
      event: '',
      eventSourceJobName: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values);
      resetForm({
        values: {
          code: '',
          description: '',
          schedulationType: '',
          cronExpression: '',
          event: '',
          eventSourceJobName: '',
        },
      });
    },
  });

  useEffect(() => {
    form.setFieldValue('schedulationType', modalData?.schedulationType);
  }, [modalData?.schedulationType]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <PmiDialog
      fullScreen={fullScreen}
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        Schedulazione Job
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <PmiTextField
              id="code"
              label="Codice"
              aria-label="Codice"
              fullWidth
              disabled
              value={jobName || ''}
            />
          </Grid>
          <Grid item xs={12}>
            <SchedulationTypeList
              value={form.values?.schedulationType || ''}
              onChange={(event) => form.setFieldValue('schedulationType', event.target.value)}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12}>
            <PmiTextField
              id="description"
              label="Descrizione"
              aria-label="Descrizione"
              fullWidth
              disabled={disabled}
              value={form.values?.description || ''}
              onChange={(event) => form.setFieldValue('description', event.target.value)}
            />
          </Grid>

          {form.values?.schedulationType === 'TIME' ? (
            <Grid item xs={12}>
              <PmiTextField
                id="cronExpression"
                label="Specifiche schedulazione"
                aria-label="Specifiche schedulazione"
                fullWidth
                disabled={disabled}
                value={form.values?.cronExpression || ''}
                onChange={(event) => form.setFieldValue('cronExpression', event.target.value)}
              />
            </Grid>
          ) : undefined}

          {form.values?.schedulationType === 'EVENT' ? (
            <>
              <Grid item xs={12}>
                <EventJobList
                  value={form.values.event || ''}
                  disabled={disabled}
                  onChange={(event) => form.setFieldValue('event', event?.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <PmiTextField
                  id="eventSourceJobName"
                  label="Nome del  Job precedente"
                  aria-label="Evento"
                  fullWidth
                  disabled={disabled}
                  value={form.values?.eventSourceJobName || ''}
                  onChange={(e) => form.setFieldValue('eventSourceJobName', e.target.value)}
                />
              </Grid>
            </>
          ) : undefined}
        </Grid>
      </DialogContent>
      <DialogActions>
        <PmiButton
          variant="contained"
          color="primary"
          type="submit"
          disabled={disabled}
          onClick={() => {
            form.handleSubmit();
          }}
        >
          CONFERMA
        </PmiButton>
        <PmiButton variant="outlined" color="cdmBrand" type="button" onClick={() => onClose(false)}>
          ANNULLA
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

ModalInsertSchedulationData.displayName = 'Modal insert schedulation data';

export default ModalInsertSchedulationData;
